/**
 * <ul is="product-card-swatches">
 *
 * Quick add by color swatch on a product card.
 * - Clicking a swatch does NOT navigate to the product page.
 * - It marks the swatch as selected, swaps the card's main image to the
 *   selected color and points the card's add-to-cart form at that variant.
 *
 * Each swatch (`[data-card-swatch]`) carries:
 *   data-variant-id        representative (first available) variant id for the color
 *   data-variant-available "true" | "false" availability of that color
 *   data-variant-image     product image url for the color (optional)
 *   data-selected          "true" on the swatch matching the default variant
 *
 * The href on the swatch is a canonical product-page fallback. Variant choice
 * stays in data attributes so the initial HTML does not expose crawlable
 * ?variant= links.
 */
if (!customElements.get('product-card-swatches')) {
  class ProductCardSwatches extends HTMLUListElement {
    constructor() {
      super();
      this._onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.swatches = Array.from(this.querySelectorAll('[data-card-swatch]'));
      if (!this.swatches.length) return;

      this.card = this.closest('.product-card');
      if (this.card) {
        this.mainImage = this.card.querySelector('.product-card__image--main img');
        this.form = this.card.querySelector('[data-card-product-form]');
        this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
        this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;
        this.atcText = this.form ? this.form.querySelector('.product-card__atc-text') : null;
      }

      // Highlight the server-rendered default color without re-swapping anything.
      const defaultSwatch = this.swatches.find((node) => node.dataset.selected === 'true');
      if (defaultSwatch) {
        defaultSwatch.classList.add('is-selected');
        defaultSwatch.setAttribute('aria-current', 'true');
      }

      this.removeEventListener('click', this._onClick);
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }

    onClick(event) {
      const swatch = event.target.closest('[data-card-swatch]');
      if (!swatch || !this.contains(swatch)) return;

      // Let modifier / non-primary clicks fall through to the href so the
      // variant can still be opened in a new tab.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      this.select(swatch);
    }

    select(swatch) {
      this.swatches.forEach((node) => {
        const isActive = node === swatch;
        node.classList.toggle('is-selected', isActive);
        if (isActive) {
          node.setAttribute('aria-current', 'true');
        } else {
          node.removeAttribute('aria-current');
        }
      });

      this.swapImage(swatch.dataset.variantImage);
      this.updateForm(swatch);
    }

    swapImage(url) {
      if (!url || !this.mainImage) return;
      if (this.mainImage.getAttribute('src') === url) return;
      // A single explicit src wins over the responsive srcset.
      this.mainImage.removeAttribute('srcset');
      this.mainImage.removeAttribute('data-srcset');
      this.mainImage.src = url;
    }

    updateForm(swatch) {
      if (!this.form || !this.idInput) return;

      const variantId = swatch.dataset.variantId;
      const available = swatch.dataset.variantAvailable === 'true';

      if (variantId) {
        this.idInput.value = variantId;
        this.idInput.disabled = !available;
      }

      if (this.submitButton) {
        this.submitButton.disabled = !available;
        this.submitButton.toggleAttribute('aria-disabled', !available);
      }

      if (this.atcText) {
        const addText = this.atcText.dataset.addText;
        const soldText = this.atcText.dataset.soldText;
        if (addText && soldText) {
          this.atcText.textContent = available ? addText : soldText;
        }
      }
    }
  }

  customElements.define('product-card-swatches', ProductCardSwatches, { extends: 'ul' });
}
